/**
 * ArvetioLogo — znak marki Arvetio ("Ascent A") jako komponent React.
 *
 * <ArvetioLogo variant="admin" size={40} />            // sam znak
 * <ArvetioLogo variant="klient" size={40} wordmark />  // znak + "arvetio"
 * <ArvetioLogo variant="admin" size={40} wordmark tagline /> // + Elevate Your Microsoft 365
 *
 * variant: "admin" | "klient" | "accessaudit" | "nis2" | "governance"
 * onDark:  true, gdy logo lezy na ciemnym tle (jasniejszy wordmark/akcent)
 */

export const ARVETIO_PALETTES = {
  admin:       { tile: "#0F2440", s1: "#4DD8F0", s2: "#00C4E8", s3: "#FFFFFF", accent: "#00A8C8" },
  klient:      { tile: "#0B3B34", s1: "#6EE7B7", s2: "#10B981", s3: "#FFFFFF", accent: "#0E9F76" },
  accessaudit: { tile: "#241B4A", s1: "#C4B5FD", s2: "#8B5CF6", s3: "#FFFFFF", accent: "#7C4DEF" },
  nis2:        { tile: "#3A2A0E", s1: "#FCD34D", s2: "#F59E0B", s3: "#FFFFFF", accent: "#D98A0B" },
  // ARVETIO Access Governance for Microsoft 365 (product colour: blue)
  governance:  { tile: "#0B2545", s1: "#93C5FD", s2: "#3B82F6", s3: "#FFFFFF", accent: "#2563EB" },
};

const STRIPES = [
  "M-6,-20 L-19,17 L-11,17 L1,-15 Z",
  "M0,-20 L-12,17 L-5,17 L7,-15 Z",
  "M6,-20 L19,17 L12,17 L1,-15 Z",
];

export default function ArvetioLogo({
  variant = "admin",
  size = 40,
  tile = true,        // false = sam znak bez kafelka
  wordmark = false,
  tagline = false,
  onDark = false,
  className,
}) {
  const p = ARVETIO_PALETTES[variant] ?? ARVETIO_PALETTES.admin;
  const nameColor = onDark ? "#FFFFFF" : "#0F2440";
  const ioColor = onDark ? p.s2 : p.accent;

  const mark = tile ? (
    <svg width={size} height={size} viewBox="0 0 60 60" aria-hidden="true">
      <rect width="60" height="60" rx="13" fill={p.tile} />
      <g transform="translate(30,31)">
        {STRIPES.map((d, i) => (
          <path key={i} d={d} fill={[p.s1, p.s2, p.s3][i]} />
        ))}
      </g>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 44 44" aria-hidden="true">
      <g transform="translate(22,24)">
        {STRIPES.map((d, i) => (
          <path key={i} d={d} fill={[p.tile, p.s2, p.s1][i]} />
        ))}
      </g>
    </svg>
  );

  if (!wordmark) return <span className={className}>{mark}</span>;

  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.25 }}
    >
      {mark}
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
        <span
          style={{
            fontSize: size * 0.62,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: nameColor,
            fontFamily: "'Segoe UI', Inter, system-ui, sans-serif",
          }}
        >
          arvet<span style={{ color: ioColor }}>io</span>
        </span>
        {tagline && (
          <span
            style={{
              fontSize: size * 0.17,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: onDark ? "#7A93B8" : "#5A6B85",
              fontFamily: "'Segoe UI', Inter, system-ui, sans-serif",
            }}
          >
            Elevate Your Microsoft 365
          </span>
        )}
      </span>
    </span>
  );
}
